import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TenantStatusViewStore } from "../../store";
import { TenantStatusView } from "../view/TenantStatusView";
import { TenantStatusUpdateStore } from "../../store";
import { TenantStatusUpdate } from "../update/TenantStatusUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TenantStatusModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TenantStatusModalContent: React.FC<TenantStatusModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TenantStatusUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TenantStatusViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TenantStatusView
            tenantStatusViewStore={storeInfo.store as TenantStatusViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <TenantStatusUpdate
            tenantStatusUpdateStore={storeInfo.store as TenantStatusUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
